import PropTypes from 'prop-types';
import React from 'react';
import { Redirect, Route } from 'react-router-dom';
import { withAuthenticationRequired } from '@auth0/auth0-react';

// lazy load all the views
const Dashboard = React.lazy( () => import( './pages/dashboards/' ) );
const ErrorPage = React.lazy( () => import( './pages/ErrorPage' ) );

// brands
// const BrandForm = React.lazy( () => import( './pages/brands/Form' ) );
// const BrandList = React.lazy( () => import( './pages/brands/List' ) );

// users and profiles
const ProfileForm = React.lazy( () => import( './pages/profiles/Form' ) );
const ProfileList = React.lazy( () => import( './pages/profiles/List' ) );
const UserForm = React.lazy( () => import( './pages/users/Form' ) );
const UserList = React.lazy( () => import( './pages/users/List' ) );

// groups
const GroupForm = React.lazy( () => import( './pages/groups/Form' ) );
const GroupList = React.lazy( () => import( './pages/groups/List' ) );

// teams
const TeamForm = React.lazy( () => import( './pages/teams/Form' ) );
const TeamList = React.lazy( () => import( './pages/teams/List' ) );

// awards
const AwardForm = React.lazy( () => import( './pages/awards/Form' ) );
const AwardList = React.lazy( () => import( './pages/awards/List' ) );

// attributes
const AttributeList = React.lazy( () => import( './pages/attributes/List' ) );

// DealTrack
const DealTrackLandingPage = React.lazy( () => import( './pages/dealtrack/Landing' ) );

// exclusive listings
const ExclusiveListingsForm = React.lazy( () => import( './pages/exclusive-listings/Form' ) );
const ExclusiveListingsList = React.lazy( () => import( './pages/exclusive-listings/List' ) );

// offices
const OfficeForm = React.lazy( () => import( './pages/offices/Form' ) );
const OfficeList = React.lazy( () => import( './pages/offices/List' ) );

// stats
const GroupStatForm = React.lazy( () => import( './pages/group-stats/Form' ) );
const GroupStatList = React.lazy( () => import( './pages/group-stats/List' ) );
const UserStatForm = React.lazy( () => import( './pages/user-stats/Form' ) );
const UserStatList = React.lazy( () => import( './pages/user-stats/List' ) );

// invitations
// const InvitationForm = React.lazy( () => import( './pages/invitations/Form' ) );
// const InvitationList = React.lazy( () => import( './pages/invitations/List' ) );
// const InvitationEmail = React.lazy( () => import( './pages/invitations/Email' ) );
// const InvitationStats = React.lazy( () => import( './pages/invitations/Stats' ) );
// const EmailTemplate = React.lazy( () => import( './pages/invitations/EmailTemplate' ) );
// const EmailSignature = React.lazy( () => import( './pages/invitations/EmailSignature' ) );

// postal code relocation tool
// const PostalCodeRelocationToolForm = React.lazy( () => import( './pages/postal-code-relocation-tool/Form' ) );

// logout
const Logout = React.lazy( () => import( './pages/Logout' ) );

// query and cost reports for all users
// const ReportsForm = React.lazy( () => import( './pages/reports/Form' ) );

// handle auth and authorization
const PrivateRoute = ( { component: Component, ...rest } ) => (
	<Route {...rest} render={props => {
		return <Component {...props} />;
	}} />
);

PrivateRoute.propTypes = {
	component: PropTypes.object,
	location: PropTypes.object,
	roles: PropTypes.array,
};

const routes = [
	// profiles and users
	{ path: '/profiles', name: 'Profiles', component: withAuthenticationRequired( ProfileList ), route: PrivateRoute, exact: true },
	{ path: '/profiles/new', name: 'New Profile', component: withAuthenticationRequired( ProfileForm ), route: PrivateRoute },
	{ path: '/profiles/:id/edit', name: 'Edit Profile', component: withAuthenticationRequired( ProfileForm ), route: PrivateRoute },
	{ path: '/users', name: 'Users', component: withAuthenticationRequired( UserList ), route: PrivateRoute, exact: true, roles: ['SuperAdmin', 'Admin'] },
	{ path: '/users/:id/edit', name: 'Edit User', component: withAuthenticationRequired( UserForm ), route: PrivateRoute, roles: ['SuperAdmin', 'Admin'] },

	// brands
	// { path: '/brands', name: 'Brands', component: BrandList, route: PrivateRoute, exact: true, roles: ['SuperAdmin'] },
	// { path: '/brands/new', name: 'Brands', component: BrandList, route: PrivateRoute, roles: ['SuperAdmin'] },
	// { path: '/brands/:id/edit', name: 'Edit Brand', component: BrandForm, route: PrivateRoute, roles: ['SuperAdmin'] },

	// groups
	{ path: '/brokerages', name: 'Brokerages', component: withAuthenticationRequired( GroupList ), route: PrivateRoute, exact: true },
	{ path: '/brokerages/:id/edit', name: 'Edit Brokerage', component: withAuthenticationRequired( GroupForm ), route: PrivateRoute },

	// teams
	{ path: '/teams', name: 'Teams', component: withAuthenticationRequired( TeamList ), route: PrivateRoute, exact: true },
	{ path: '/teams/new', name: 'New Team', component: withAuthenticationRequired( TeamForm ), route: PrivateRoute },
	{ path: '/teams/:id/edit', name: 'Edit Team', component: withAuthenticationRequired( TeamForm ), route: PrivateRoute },

	// awards
	{ path: '/awards', name: 'Awards', component: withAuthenticationRequired( AwardList ), route: PrivateRoute, exact: true },
	{ path: '/awards/new', name: 'New Award', component: withAuthenticationRequired( AwardForm ), route: PrivateRoute },
	{ path: '/awards/:id/edit', name: 'Edit Award', component: withAuthenticationRequired( AwardForm ), route: PrivateRoute },

	// profile attributes
	{ path: '/profile-attributes', name: 'Attributes', component: withAuthenticationRequired( AttributeList ), route: PrivateRoute },

	{ path: '/dealtrack', name: 'DealTrack', component: withAuthenticationRequired( DealTrackLandingPage ), route: PrivateRoute },

	// exclusive listings
	{ path: '/exclusive-listings', name: 'Exclusive Listings', component: withAuthenticationRequired( ExclusiveListingsList ), route: PrivateRoute, exact: true },
	{ path: '/exclusive-listings/new', name: 'New Exclusive Listing', component: withAuthenticationRequired( ExclusiveListingsForm ), route: PrivateRoute },
	{ path: '/exclusive-listings/:id/edit', name: 'Edit Exclusive Listing', component: withAuthenticationRequired( ExclusiveListingsForm ), route: PrivateRoute },

	// offices
	{ path: '/offices', name: 'Offices', component: withAuthenticationRequired( OfficeList ), route: PrivateRoute, exact: true },
	{ path: '/offices/new', name: 'New Office', component: withAuthenticationRequired( OfficeForm ), route: PrivateRoute },
	{ path: '/offices/:id/edit', name: 'Edit Office', component: withAuthenticationRequired( OfficeForm ), route: PrivateRoute },

	// stats
	{ path: '/group-stats', name: 'Stats', component: withAuthenticationRequired( GroupStatList ), route: PrivateRoute, exact: true },
	{ path: '/group-stats/new', name: 'New Stat', component: withAuthenticationRequired( GroupStatForm ), route: PrivateRoute },
	{ path: '/group-stats/:id/edit', name: 'Edit Stat', component: withAuthenticationRequired( GroupStatForm ), route: PrivateRoute },
	{ path: '/user-stats', name: 'Stats', component: withAuthenticationRequired( UserStatList ), route: PrivateRoute, exact: true },
	{ path: '/user-stats/new', name: 'New Stat', component: withAuthenticationRequired( UserStatForm ), route: PrivateRoute },
	{ path: '/user-stats/:id/edit', name: 'Edit Stat', component: withAuthenticationRequired( UserStatForm ), route: PrivateRoute },

	// invitations
	// { path: '/invitations', name: 'Invitations', component: withAuthenticationRequired( InvitationList ), route: PrivateRoute, exact: true },
	// { path: '/invitations/new', name: 'New Invitation', component: withAuthenticationRequired( InvitationForm ), route: PrivateRoute, exact: true },
	// { path: '/invitations/:id/edit', name: 'Edit Invitation', component: withAuthenticationRequired( InvitationForm ), route: PrivateRoute, exact: true },
	// { path: '/invitations/:id/email', name: 'Send Email', component: withAuthenticationRequired( InvitationEmail ), route: PrivateRoute },
	// { path: '/invitations/:id/stats', name: 'View Stats', component: withAuthenticationRequired( InvitationStats ), route: PrivateRoute },
	// { path: '/invitations/email-signature', name: 'Email Signature', component: withAuthenticationRequired( EmailSignature ), route: PrivateRoute, exact: true },
	// { path: '/invitations/email-template', name: 'Email Template', component: withAuthenticationRequired( EmailTemplate ), route: PrivateRoute, exact: true },

	// postal code tool form
	// { path: '/postal-code-relocation-tool', name: 'Postal Code Relocation Tool', component: withAuthenticationRequired( PostalCodeRelocationToolForm ), route: PrivateRoute, exact: true },

	// { path: '/reports', name: 'Reports', component: withAuthenticationRequired( ReportsForm ), route: PrivateRoute, exact: true },

	// other pages
	{ path: '/logout', name: 'Logout', component: withAuthenticationRequired( Logout ), route: PrivateRoute, exact: true },
	{ path: '/dashboard', name: 'Dashboard', component: withAuthenticationRequired( Dashboard ), route: PrivateRoute },
	{
		path: '/',
		exact: true,
		component: () => <Redirect to="/dashboard" />, // eslint-disable-line react/display-name
		route: PrivateRoute,
	},
	{
		path: '/force-error',
		exact: true,
		component: () => { throw new Error( 'Oops!' ); }, // eslint-disable-line react/display-name
		route: PrivateRoute,
	},
	{ name: 'Not Found', component: ErrorPage, route: Route },
];

export { routes, PrivateRoute };
